import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Skel } from "@/components/app/Common";
import { api, get, errMsg } from "@/lib/api";

export function Answer({ q, value, onChange }) {
  if (q.kind === "short") return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={q.prompt} data-testid={`extra-input-${q.id}`} />;
  if (q.kind === "text") return <Textarea rows={5} value={value || ""} onChange={(e) => onChange(e.target.value)} aria-label={q.prompt} data-testid={`extra-input-${q.id}`} />;
  const multi = q.kind === "multi";
  const sel = multi ? value || [] : value;
  const toggle = (id) => onChange(multi ? (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]) : id);
  return (
    <fieldset className="space-y-2"><legend className="sr-only">{q.prompt}</legend>
      {multi && <p className="text-xs text-ink-muted">Choose all that apply.</p>}
      {q.options.map((o) => (
        <label key={o.id} className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${(multi ? sel.includes(o.id) : sel === o.id) ? "border-primary bg-primary/5" : "border-line hover:bg-surface-2"}`}>
          <input type={multi ? "checkbox" : "radio"} name={q.id} checked={multi ? sel.includes(o.id) : sel === o.id} onChange={() => toggle(o.id)} data-testid={`extra-opt-${q.id}-${o.id}`} />
          {o.text}</label>
      ))}
    </fieldset>
  );
}

export function AssessExtra({ token, info, part = "post", onDone }) {
  const { data } = useQuery({ queryKey: ["assess-extra", token], queryFn: () => get(`/public/assess/${token}/extra`) });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setAnswers(data.answers); }, [data]);
  if (!data) return <Skel className="h-72" />;
  const pre = part === "pre";
  const sections = pre ? data.sections.slice(0, data.split) : data.sections.slice(data.split);
  const all = sections.flatMap((s) => s.questions);
  const next = () => (info?.sjt_count ? "sjt" : info?.extra_count > (info?.extra_pre || 0) ? "extra" : null);
  const submit = async () => {
    const miss = all.filter((q) => q.required && !(Array.isArray(answers[q.id]) ? answers[q.id].length : answers[q.id]));
    if (miss.length) { toast.error(`${miss.length} required question(s) still need an answer.`); return; }
    setBusy(true);
    try {
      await api.post(`/public/assess/${token}/extra`, { answers });
      if (pre && next()) { onDone(next()); setBusy(false); return; }
      await api.post(`/public/assess/${token}/submit`);
      onDone("done");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-5" data-testid="assess-extra">
      {sections.map((s) => (
        <section key={s.id} className="panel space-y-5 p-5 sm:p-6" data-testid={`extra-section-${s.id}`}>
          <h2 className="text-lg font-semibold">{s.title}</h2>
          {s.questions.map((q, i) => (
            <div key={q.id} className="space-y-2" data-testid={`extra-q-${q.id}`}>
              <p className="font-medium">{i + 1}. {q.prompt}{q.required && <span className="text-risk" aria-label="required"> *</span>}</p>
              <Answer q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
            </div>
          ))}
        </section>
      ))}
      <Button className="w-full sm:w-auto" disabled={busy} onClick={submit} data-testid={pre ? "extra-continue" : "extra-submit"}>{pre && next() ? "Save and continue" : "Submit my answers"}</Button>
    </div>
  );
}
